import { LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { genBlock } from "@/utils/bem";
import {
  Box,
  chakra,
  Flex,
  SystemStyleObject,
  useRadio,
  useRadioGroup,
  UseRadioGroupProps,
  UseRadioProps,
  useStyleConfig,
} from "@chakra-ui/react";
import classNames from "classnames";
import React, { ReactNode, useEffect, useRef } from "react";

const itemBlock = genBlock("c-radio-tag");

type RadioTagProps = {
  __css?: SystemStyleObject;
  children: React.ReactNode;
  size?: "sm" | "md";
  variant?: "text" | "outline" | "primary";
  className?: string;
  getRef?: (el: HTMLDivElement | null) => void;
} & UseRadioProps;

// 1. Create a component that consumes the `useRadio` hook
export const RadioTag: React.FC<RadioTagProps> = ({
  __css,
  children,
  size = "sm",
  variant = "text",
  className,
  getRef,
  ...radioProps
}) => {
  const { getInputProps, getCheckboxProps } = useRadio(radioProps);
  const ref = useRef<HTMLDivElement | null>(null);
  const input = getInputProps();
  const checkbox = getCheckboxProps();
  const styles = useStyleConfig("Button", { variant, size });
  const { id, ...restInputProps } = input;

  useEffect(() => {
    if (getRef) {
      getRef(ref.current);
    }
  }, [getRef, ref.current]);

  return (
    <Box ref={ref as any} as="label" id={id} className={classNames(itemBlock(), className)}>
      <input {...restInputProps} id={id ? `${id}_INPUT` : undefined} />
      <Box
        {...checkbox}
        id={`${id}_CHECKBOX`}
        __css={{ ...styles, ...__css }}
        textAlign="center"
        borderRadius="2px"
        _checked={{
          color: "brand.primary",
          borderColor: "brand.primary",
          bg: "transparent",
        }}
        className={itemBlock({ e: "content" })}
      >
        {children}
      </Box>
    </Box>
  );
};
const block = genBlock("c-radio-tag-group");
// Step 2: Use the `useRadioGroup` hook to control a group of custom radios.

export type RadioOptionType<T extends string | number = string | number> = {
  value: T;
  label?: ReactNode;
  render?: React.FunctionComponent<RadioOptionType>;
  disabled?: boolean;
  css?: SystemStyleObject;
  [index: string]: any;
};

const disabledStyle = {
  cursor: "not-allowed",
};
const IRadioTagGroup: React.FC<{
  className?: string;
  options: RadioOptionType[];
  title?: ReactNode;
  useRadioGroupProps?: UseRadioGroupProps;
  disabled?: boolean;
}> = ({ options = [], className, title, useRadioGroupProps, disabled }) => {
  const { getRootProps, getRadioProps } = useRadioGroup(useRadioGroupProps);

  const group = getRootProps();

  return (
    <Flex className={classNames(className, block())} w="100%" gap="8px" alignItems="baseline">
      {title ? (
        <LabelText className={block("title")} whiteSpace="nowrap" color="#FFFFFF" fontWeight={500}>
          {title}
        </LabelText>
      ) : null}
      <Flex
        className={block({ e: "options" })}
        wrap="wrap"
        flex={1}
        gap="8px"
        {...group}
        {...(disabled ? disabledStyle : {})}
      >
        {options.map((option) => {
          const { value, label, render, css, getRef } = option;
          const radioProps = getRadioProps({ value: value, id: `${value}_TAG` });
          const CustomLabel = render;
          return (
            <RadioTag
              getRef={getRef}
              key={value}
              variant="outline"
              __css={{
                minH: "24px",
                h: "fit-content",
                rounded: "4px",
                borderColor: "text.placeholder",
                color: "text.placeholder",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                _hover: {
                  color: "#cccccc",
                  borderColor: "#cccccc",
                },
                _active: {},
                _disabled: {
                  opacity: 0.4,
                  cursor: "not-allowed",
                  _checked: {
                    opacity: 1,
                  },
                },
                ...(css ?? {}),
              }}
              className={block({ e: "item" })}
              isDisabled={option.disabled ?? disabled}
              {...radioProps}
            >
              {CustomLabel ? <CustomLabel {...option} /> : label}
            </RadioTag>
          );
        })}
      </Flex>
    </Flex>
  );
};

export const RadioTagGroup = chakra(IRadioTagGroup);
